import React, { useState, useEffect } from 'react';
import { 
  HiOutlineUser, HiOutlineBell, HiOutlinePencil, HiOutlineCheck, 
  HiOutlineX, HiOutlinePlus, HiOutlineTrash, HiOutlineClipboard 
} from 'react-icons/hi';
// import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';
import { UserProfile, UserPreferences } from '../types';
import { settingsService } from '../services/settingsService';
import toast from 'react-hot-toast';

interface Tab {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}

const Settings: React.FC = () => {
  // const { logout } = useAuth();
  const [activeTab, setActiveTab] = useState<string>('profile');
  const [loading, setLoading] = useState<boolean>(true);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  
  const [profileData, setProfileData] = useState<UserProfile>({
    _id: '',
    name: '',
    email: '',
    phone: '',
    avatar: '',
    bio: '',
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: ''
    },
    education: [],
    workExperience: [],
    skills: [],
    socialLinks: {
      linkedin: '',
      github: '',
      twitter: '',
      leetcode: '',
      portfolio: '',
      website: ''
    },
    certifications: [],
    languages: [],
    createdAt: '',
    updatedAt: ''
  });
  
  const [preferences, setPreferences] = useState<UserPreferences>({
    theme: 'light',
    notifications: {
      email: true,
      push: true,
      sms: false
    },
    privacy: {
      profileVisible: true,
      showEmail: true,
      showPhone: false
    }
  });

  const tabs: Tab[] = [
    { id: 'profile', label: 'Profile', icon: HiOutlineUser },
    { id: 'notifications', label: 'Notifications', icon: HiOutlineBell },
  ];

  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async (): Promise<void> => {
    try {
      const [profileRes, preferencesRes] = await Promise.all([
        settingsService.getUserProfile(),
        settingsService.getUserPreferences()
      ]);

      // Ensure socialLinks are properly initialized
      const processedProfile = {
        ...profileRes,
        socialLinks: {
          linkedin: '',
          github: '',
          twitter: '',
          leetcode: '',
          portfolio: '',
          website: '',
          ...profileRes.socialLinks
        },
        address: {
          street: '',
          city: '',
          state: '',
          zipCode: '',
          country: '',
          ...profileRes.address
        }
      };

      setProfileData(processedProfile);
      
      if (preferencesRes) {
        setPreferences((prev: UserPreferences) => ({ ...prev, ...preferencesRes }));
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      toast.error('Failed to fetch user data');
    } finally {
      setLoading(false);
    }
  };

  const handleProfileUpdate = async (): Promise<void> => {
    try {
      const updatedProfile = await settingsService.updateUserProfile(profileData);
      setProfileData(updatedProfile);
      setIsEditing(false);
      toast.success('Profile updated successfully');
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile');
    }
  };

  const handlePreferencesUpdate = async (): Promise<void> => {
    try {
      await settingsService.updateUserPreferences(preferences);
      toast.success('Preferences updated successfully');
    } catch (error) {
      console.error('Error updating preferences:', error);
      toast.error('Failed to update preferences');
    }
  };

  const copyToClipboard = async (text: string, label: string) => {
    if (!text || text === 'Not specified') return;
    
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied to clipboard!`, {
        duration: 2000,
        position: 'bottom-right',
      });
    } catch (err) {
      console.error('Failed to copy text: ', err);
      toast.error('Failed to copy to clipboard');
    }
  };

  const formatMonthYear = (dateString: string): string => {
    if (!dateString || dateString === 'Not specified') return 'Not specified';
    
    try {
      let date: Date;
      
      // Handle ISO date strings (from backend)
      if (dateString.includes('T') || dateString.endsWith('Z')) {
        date = new Date(dateString);
      }
      // Handle YYYY-MM format (from month input)
      else if (dateString.match(/^\d{4}-\d{2}$/)) {
        date = new Date(dateString + '-01');
      }
      // Handle YYYY-MM-DD format  
      else if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
        date = new Date(dateString);
      }
      // Handle other formats
      else {
        date = new Date(dateString);
      }
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        console.warn('Invalid date:', dateString);
        return 'Invalid date';
      }
      
      const options: Intl.DateTimeFormatOptions = { 
        year: 'numeric', 
        month: 'long' 
      };
      return date.toLocaleDateString('en-US', options);
    } catch (error) {
      console.error('Date formatting error for:', dateString, error);
      return 'Invalid date';
    }
  };

  const formatDateForInput = (dateString: string): string => {
    if (!dateString) return '';
    
    try {
      let date: Date;
      
      // Handle ISO date strings (from backend)
      if (dateString.includes('T') || dateString.endsWith('Z')) {
        date = new Date(dateString);
      }
      // Handle existing formats
      else if (dateString.match(/^\d{4}-\d{2}$/)) {
        return dateString; // Already in correct format
      }
      else if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
        date = new Date(dateString);
      }
      else {
        date = new Date(dateString);
      }
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return '';
      }
      
      // Format as YYYY-MM for month input
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      return `${year}-${month}`;
    } catch (error) {
      console.error('Date input formatting error for:', dateString, error);
      return '';
    }
  };

  const CopyableText: React.FC<{ text: string; label: string; className?: string; isLink?: boolean }> = ({ 
    text, 
    label, 
    className = "text-secondary-900", 
    isLink = false 
  }) => {
    if (!text || text === 'Not specified') {
      return <p className="text-secondary-500">Not specified</p>;
    }

    return (
      <div className="group relative">
        {isLink ? (
          <div className="flex items-center gap-2 p-2 -m-2 rounded-lg hover:bg-primary-50 border border-transparent transition-all duration-300 ease-out">
            <a 
              href={text} 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-primary-600 hover:text-primary-700 hover:underline flex-1 transition-colors duration-300 ease-out"
            >
              {text}
            </a>
            <button
              onClick={() => copyToClipboard(text, label)}
              className="opacity-0 group-hover:opacity-80 transition-all duration-300 ease-out p-1.5 hover:bg-primary-100 rounded-md text-primary-500 hover:text-primary-600"
            >
              <HiOutlineClipboard className="h-3 w-3" />
            </button>
          </div>
        ) : (
          <div
            className={`${className} cursor-pointer hover:bg-gray-100 p-1.5 -m-1.5 rounded-md transition-all duration-300 ease-out flex items-center justify-between group`}
            onClick={() => copyToClipboard(text, label)}
          >
            <span className="transition-colors duration-300 ease-out">{text}</span>
            <HiOutlineClipboard className="h-3 w-3 text-secondary-400 opacity-0 group-hover:opacity-90 group-hover:text-secondary-700 transition-all duration-300 ease-out" />
          </div>
        )}
      </div>
    );
  };

  const addEducation = () => {
    setProfileData({
      ...profileData,
      education: [
        ...(profileData.education || []),
        {
          institution: '',
          degree: '',
          fieldOfStudy: '',
          startDate: '',
          endDate: '',
          gpa: 0,
          description: '',
          isCurrentlyEnrolled: false
        }
      ]
    });
  };

  const removeEducation = (index: number) => {
    setProfileData({
      ...profileData,
      education: profileData.education?.filter((_, i) => i !== index) || []
    });
  };

  const updateEducation = (index: number, field: string, value: any) => {
    const updatedEducation = [...(profileData.education || [])];
    updatedEducation[index] = { ...updatedEducation[index], [field]: value };
    setProfileData({ ...profileData, education: updatedEducation });
  };

  const addWorkExperience = () => {
    setProfileData({
      ...profileData,
      workExperience: [
        ...(profileData.workExperience || []),
        {
          company: '',
          position: '',
          startDate: '',
          endDate: '',
          description: '',
          location: '',
          isCurrentlyWorking: false
        }
      ]
    });
  };

  const removeWorkExperience = (index: number) => {
    setProfileData({
      ...profileData,
      workExperience: profileData.workExperience?.filter((_, i) => i !== index) || []
    });
  };

  const updateWorkExperience = (index: number, field: string, value: any) => {
    const updatedWork = [...(profileData.workExperience || [])];
    updatedWork[index] = { ...updatedWork[index], [field]: value };
    setProfileData({ ...profileData, workExperience: updatedWork });
  };

  const addSkill = () => {
    setProfileData({
      ...profileData,
      skills: [
        ...(profileData.skills || []),
        {
          name: '',
          level: 'beginner',
          category: 'technical'
        }
      ]
    });
  };

  const removeSkill = (index: number) => {
    setProfileData({
      ...profileData,
      skills: profileData.skills?.filter((_, i) => i !== index) || []
    });
  };

  const updateSkill = (index: number, field: string, value: any) => {
    const updatedSkills = [...(profileData.skills || [])];
    updatedSkills[index] = { ...updatedSkills[index], [field]: value };
    setProfileData({ ...profileData, skills: updatedSkills });
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-secondary-900">Settings</h1>
        <p className="mt-1 text-sm text-secondary-600">
          Manage your account settings and preferences.
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-secondary-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-secondary-500 hover:text-secondary-700 hover:border-secondary-300'
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="card">
        <div className="card-content">
          {activeTab === 'profile' && (
            <div>
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-medium text-secondary-900">Profile Information</h3>
                <div className="flex gap-2 mt-4">
                  {isEditing ? (
                    <>
                      <button
                        onClick={handleProfileUpdate}
                        className="btn btn-primary btn-sm flex items-center gap-2"
                      >
                        <HiOutlineCheck className="h-4 w-4" />
                        Save
                      </button>
                      <button
                        onClick={() => {
                          setIsEditing(false);
                          fetchUserData(); // Reset to original data
                        }}
                        className="btn btn-secondary btn-sm flex items-center gap-2"
                      >
                        <HiOutlineX className="h-4 w-4" />
                        Cancel
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => setIsEditing(true)}
                      className="btn btn-primary btn-sm flex items-center gap-2"
                    >
                      <HiOutlinePencil className="h-4 w-4" />
                      Edit Profile
                    </button>
                  )}
                </div>
              </div>

              <div className="space-y-8">
                {/* Basic Information */}
                <div className="bg-secondary-50 p-6 rounded-lg border border-secondary-100">
                  <h4 className="text-lg font-semibold text-secondary-900 mb-6 flex items-center">
                    <HiOutlineUser className="h-5 w-5 mr-2 text-primary-600" />
                    Basic Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-2">
                        Name
                      </label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={profileData.name}
                          onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                          className="input w-full"
                          placeholder="Your name"
                        />
                      ) : (
                        <CopyableText text={profileData.name || 'Not specified'} label="Name" />
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-2">
                        Email
                      </label>
                      {isEditing ? (
                        <p className="text-secondary-900 p-1">{profileData.email}</p>
                      ) : (
                        <CopyableText text={profileData.email} label="Email" />
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-2">
                        Phone
                      </label>
                      {isEditing ? (
                        <input
                          type="tel"
                          value={profileData.phone || ''}
                          onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                          className="input w-full"
                          placeholder="Your phone number"
                        />
                      ) : (
                        <CopyableText text={profileData.phone || 'Not specified'} label="Phone" />
                      )}
                    </div>
                  </div>
                  
                  <div className="mt-6">
                    <label className="block text-sm font-medium text-secondary-700 mb-2">
                      Bio
                    </label>
                    {isEditing ? (
                      <textarea
                        value={profileData.bio || ''}
                        onChange={(e) => setProfileData({ ...profileData, bio: e.target.value })}
                        className="textarea w-full"
                        rows={3}
                        placeholder="Tell us about yourself"
                      />
                    ) : (
                      <CopyableText text={profileData.bio || 'Not specified'} label="Bio" />
                    )}
                  </div>
                </div>

                <hr className="border-secondary-200 my-8" />

                {/* Address */}
                <div className="bg-secondary-50 p-6 rounded-lg border border-secondary-100">
                  <h4 className="text-lg font-semibold text-secondary-900 mb-6">Address</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-secondary-700 mb-2">
                        Street Address
                      </label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={profileData.address?.street || ''}
                          onChange={(e) => setProfileData({ 
                            ...profileData, 
                            address: { ...profileData.address, street: e.target.value }
                          })}
                          className="input w-full"
                          placeholder="Street address"
                        />
                      ) : (
                        <CopyableText text={profileData.address?.street || 'Not specified'} label="Street Address" />
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-2">
                        City
                      </label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={profileData.address?.city || ''}
                          onChange={(e) => setProfileData({ 
                            ...profileData, 
                            address: { ...profileData.address, city: e.target.value }
                          })}
                          className="input w-full"
                          placeholder="City"
                        />
                      ) : (
                        <CopyableText text={profileData.address?.city || 'Not specified'} label="City" />
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-2">
                        State
                      </label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={profileData.address?.state || ''}
                          onChange={(e) => setProfileData({ 
                            ...profileData, 
                            address: { ...profileData.address, state: e.target.value }
                          })}
                          className="input w-full"
                          placeholder="State"
                        />
                      ) : (
                        <CopyableText text={profileData.address?.state || 'Not specified'} label="State" />
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-2">
                        ZIP Code
                      </label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={profileData.address?.zipCode || ''}
                          onChange={(e) => setProfileData({ 
                            ...profileData, 
                            address: { ...profileData.address, zipCode: e.target.value }
                          })}
                          className="input w-full"
                          placeholder="ZIP Code"
                        />
                      ) : (
                        <CopyableText text={profileData.address?.zipCode || 'Not specified'} label="ZIP Code" />
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-2">
                        Country
                      </label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={profileData.address?.country || ''}
                          onChange={(e) => setProfileData({ 
                            ...profileData, 
                            address: { ...profileData.address, country: e.target.value }
                          })}
                          className="input w-full"
                          placeholder="Country"
                        />
                      ) : (
                        <CopyableText text={profileData.address?.country || 'Not specified'} label="Country" />
                      )}
                    </div>
                  </div>
                </div>

                <hr className="border-secondary-200 my-8" />

                {/* Social Links */}
                <div className="bg-secondary-50 p-6 rounded-lg border border-secondary-100">
                  <h4 className="text-lg font-semibold text-secondary-900 mb-6">Social Links</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {Object.entries(profileData.socialLinks || {}).map(([key, value]) => (
                      <div key={key}>
                        <label className="block text-sm font-medium text-secondary-700 mb-2 capitalize">
                          {key}
                        </label>
                        {isEditing ? (
                          <input
                            type="url"
                            value={value || ''}
                            onChange={(e) => setProfileData({ 
                              ...profileData, 
                              socialLinks: { 
                                ...profileData.socialLinks, 
                                [key]: e.target.value 
                              }
                            })}
                            className="input w-full"
                            placeholder={`Your ${key} URL`}
                          />
                        ) : (
                          <CopyableText 
                            text={value || 'Not specified'} 
                            label={key} 
                            isLink={!!value && value !== 'Not specified'} 
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <hr className="border-secondary-200 my-8" />

                {/* Education */}
                <div className="bg-secondary-50 p-6 rounded-lg border border-secondary-100">
                  <div className="flex justify-between items-center mb-6">
                    <h4 className="text-lg font-semibold text-secondary-900">Education</h4>
                    {isEditing && (
                      <button
                        onClick={addEducation}
                        className="btn btn-outline btn-sm flex items-center gap-2"
                      >
                        <HiOutlinePlus className="h-4 w-4" />
                        Add Education
                      </button>
                    )}
                  </div>
                  <div className="space-y-6">
                    {profileData.education?.map((edu, index) => (
                      <div key={index} className="bg-white p-6 border border-secondary-200 rounded-lg shadow-sm">
                        {isEditing && (
                          <div className="flex justify-end mb-4">
                            <button
                              onClick={() => removeEducation(index)}
                              className="btn-icon btn-icon-danger"
                              title="Remove education"
                            >
                              <HiOutlineTrash className="h-4 w-4" />
                            </button>
                          </div>
                        )}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <label className="block text-sm font-medium text-secondary-700 mb-2">
                              Institution
                            </label>
                            {isEditing ? (
                              <input
                                type="text"
                                value={edu.institution}
                                onChange={(e) => updateEducation(index, 'institution', e.target.value)}
                                className="input w-full"
                                placeholder="University/College name"
                              />
                            ) : (
                              <CopyableText text={edu.institution || 'Not specified'} label="Institution" />
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-secondary-700 mb-2">
                              Degree
                            </label>
                            {isEditing ? (
                              <input
                                type="text"
                                value={edu.degree}
                                onChange={(e) => updateEducation(index, 'degree', e.target.value)}
                                className="input w-full"
                                placeholder="Bachelor's, Master's, etc."
                              />
                            ) : (
                              <CopyableText text={edu.degree || 'Not specified'} label="Degree" />
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-secondary-700 mb-2">
                              Field of Study
                            </label>
                            {isEditing ? (
                              <input
                                type="text"
                                value={edu.fieldOfStudy}
                                onChange={(e) => updateEducation(index, 'fieldOfStudy', e.target.value)}
                                className="input w-full"
                                placeholder="Computer Science, etc."
                              />
                            ) : (
                              <CopyableText text={edu.fieldOfStudy || 'Not specified'} label="Field of Study" />
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-secondary-700 mb-2">
                              CGPA
                            </label>
                            {isEditing ? (
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                max="10"
                                value={edu.gpa || ''}
                                onChange={(e) => updateEducation(index, 'gpa', parseFloat(e.target.value))}
                                className="input w-full"
                                placeholder="9.35"
                              />
                            ) : (
                              <CopyableText text={edu.gpa?.toString() || 'Not specified'} label="GPA" />
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-secondary-700 mb-2">
                              Start Date
                            </label>
                            {isEditing ? (
                              <input
                                type="month"
                                value={formatDateForInput(edu.startDate || '')}
                                onChange={(e) => updateEducation(index, 'startDate', e.target.value)}
                                className="input w-full"
                              />
                            ) : (
                              <CopyableText text={formatMonthYear(edu.startDate || '')} label="Start Date" />
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-secondary-700 mb-2">
                              End Date
                            </label>
                            {isEditing ? (
                              <input
                                type="month"
                                value={formatDateForInput(edu.endDate || '')}
                                onChange={(e) => updateEducation(index, 'endDate', e.target.value)}
                                className="input w-full"
                                disabled={edu.isCurrentlyEnrolled}
                              />
                            ) : (
                              <CopyableText 
                                text={edu.isCurrentlyEnrolled ? 'Currently enrolled' : formatMonthYear(edu.endDate || '')} 
                                label="End Date" 
                              />
                            )}
                          </div>
                        </div>
                        {isEditing && (
                          <div className="mt-6 pt-4 border-t border-secondary-100">
                            <label className="flex items-center">
                              <input
                                type="checkbox"
                                checked={edu.isCurrentlyEnrolled}
                                onChange={(e) => updateEducation(index, 'isCurrentlyEnrolled', e.target.checked)}
                                className="checkbox mr-2"
                              />
                              Currently enrolled
                            </label>
                          </div>
                        )}
                      </div>
                    )) || <p className="text-secondary-500 text-center py-8">No education history added yet.</p>}
                  </div>
                </div>

                <hr className="border-secondary-200 my-8" />

                {/* Work Experience */}
                <div className="bg-secondary-50 p-6 rounded-lg border border-secondary-100">
                  <div className="flex justify-between items-center mb-6">
                    <h4 className="text-lg font-semibold text-secondary-900">Work Experience</h4>
                    {isEditing && (
                      <button
                        onClick={addWorkExperience}
                        className="btn btn-outline btn-sm flex items-center gap-2"
                      >
                        <HiOutlinePlus className="h-4 w-4" />
                        Add Experience
                      </button>
                    )}
                  </div>
                  <div className="space-y-4">
                    {profileData.workExperience?.map((work, index) => (
                      <div key={index} className="p-4 border border-secondary-200 rounded-lg">
                        {isEditing && (
                          <div className="flex justify-end mb-2">
                            <button
                              onClick={() => removeWorkExperience(index)}
                              className="btn-icon btn-icon-danger"
                              title="Remove work experience"
                            >
                              <HiOutlineTrash className="h-4 w-4" />
                            </button>
                          </div>
                        )}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-secondary-700 mb-1">
                              Company
                            </label>
                            {isEditing ? (
                              <input
                                type="text"
                                value={work.company}
                                onChange={(e) => updateWorkExperience(index, 'company', e.target.value)}
                                className="input w-full"
                                placeholder="Company name"
                              />
                            ) : (
                              <CopyableText text={work.company || 'Not specified'} label="Company" />
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-secondary-700 mb-1">
                              Position
                            </label>
                            {isEditing ? (
                              <input
                                type="text"
                                value={work.position}
                                onChange={(e) => updateWorkExperience(index, 'position', e.target.value)}
                                className="input w-full"
                                placeholder="Job title"
                              />
                            ) : (
                              <CopyableText text={work.position || 'Not specified'} label="Position" />
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-secondary-700 mb-1">
                              Location
                            </label>
                            {isEditing ? (
                              <input
                                type="text"
                                value={work.location || ''}
                                onChange={(e) => updateWorkExperience(index, 'location', e.target.value)}
                                className="input w-full"
                                placeholder="City, State"
                              />
                            ) : (
                              <CopyableText text={work.location || 'Not specified'} label="Location" />
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-secondary-700 mb-1">
                              Start Date
                            </label>
                            {isEditing ? (
                              <input
                                type="month"
                                value={formatDateForInput(work.startDate || '')}
                                onChange={(e) => updateWorkExperience(index, 'startDate', e.target.value)}
                                className="input w-full"
                              />
                            ) : (
                              <CopyableText text={formatMonthYear(work.startDate || '')} label="Start Date" />
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-secondary-700 mb-1">
                              End Date
                            </label>
                            {isEditing ? (
                              <input
                                type="month"
                                value={formatDateForInput(work.endDate || '')}
                                onChange={(e) => updateWorkExperience(index, 'endDate', e.target.value)}
                                className="input w-full"
                                disabled={work.isCurrentlyWorking}
                              />
                            ) : (
                              <CopyableText 
                                text={work.isCurrentlyWorking ? 'Currently working' : formatMonthYear(work.endDate || '')} 
                                label="End Date" 
                              />
                            )}
                          </div>
                        </div>
                        {isEditing && (
                          <div className="mt-4">
                            <label className="flex items-center">
                              <input
                                type="checkbox"
                                checked={work.isCurrentlyWorking}
                                onChange={(e) => updateWorkExperience(index, 'isCurrentlyWorking', e.target.checked)}
                                className="checkbox mr-2"
                              />
                              Currently working here
                            </label>
                          </div>
                        )}
                        <div className="mt-4">
                          <label className="block text-sm font-medium text-secondary-700 mb-1">
                            Description
                          </label>
                          {isEditing ? (
                            <textarea
                              value={work.description || ''}
                              onChange={(e) => updateWorkExperience(index, 'description', e.target.value)}
                              className="textarea w-full"
                              rows={3}
                              placeholder="Describe your role and responsibilities"
                            />
                          ) : (
                            <CopyableText text={work.description || 'No description provided'} label="Description" />
                          )}
                        </div>
                      </div>
                    )) || <p className="text-secondary-500">No work experience added yet.</p>}
                  </div>
                </div>

                {/* Skills */}
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <h4 className="text-md font-medium text-secondary-900">Skills</h4>
                    {isEditing && (
                      <button
                        onClick={addSkill}
                        className="btn btn-outline btn-sm flex items-center gap-2"
                      >
                        <HiOutlinePlus className="h-4 w-4" />
                        Add Skill
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {profileData.skills?.map((skill, index) => (
                      <div key={index} className="p-3 border border-secondary-200 rounded-lg">
                        {isEditing ? (
                          <div>
                            <div className="flex justify-between items-start mb-2">
                              <div className="flex-1">
                                <input
                                  type="text"
                                  value={skill.name}
                                  onChange={(e) => updateSkill(index, 'name', e.target.value)}
                                  className="input w-full mb-2"
                                  placeholder="Skill name"
                                />
                                <select
                                  value={skill.level}
                                  onChange={(e) => updateSkill(index, 'level', e.target.value)}
                                  className="select w-full mb-2"
                                >
                                  <option value="beginner">Beginner</option>
                                  <option value="intermediate">Intermediate</option>
                                  <option value="advanced">Advanced</option>
                                  <option value="expert">Expert</option>
                                </select>
                                <select
                                  value={skill.category}
                                  onChange={(e) => updateSkill(index, 'category', e.target.value)}
                                  className="select w-full"
                                >
                                  <option value="technical">Technical</option>
                                  <option value="soft">Soft Skills</option>
                                  <option value="language">Language</option>
                                  <option value="other">Other</option>
                                </select>
                              </div>
                              <button
                                onClick={() => removeSkill(index)}
                                className="ml-2 btn-icon btn-icon-danger"
                                title="Remove skill"
                              >
                                <HiOutlineTrash className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div>
                            <h5 className="font-medium text-secondary-900">{skill.name}</h5>
                            <p className="text-sm text-secondary-600 capitalize">{skill.level}</p>
                            <p className="text-xs text-secondary-500 capitalize">{skill.category}</p>
                          </div>
                        )}
                      </div>
                    )) || <p className="text-secondary-500 md:col-span-2 lg:col-span-3">No skills added yet.</p>}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'notifications' && (
            <div>
              <h3 className="text-lg font-medium text-secondary-900 mb-4">Notification Preferences</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-secondary-700">Email Notifications</label>
                    <p className="text-xs text-secondary-500">Get notified via email about updates</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.notifications.email}
                      onChange={(e) => setPreferences({
                        ...preferences,
                        notifications: { ...preferences.notifications, email: e.target.checked }
                      })}
                      className="sr-only peer"
                    />
                    <div className={`toggle ${preferences.notifications.email ? 'data-[state=checked]' : 'data-[state=unchecked]'}`} data-state={preferences.notifications.email ? 'checked' : 'unchecked'}>
                      <div className="toggle-thumb"></div>
                    </div>
                  </label>
                </div>
                <div className="flex justify-end">
                  <button onClick={handlePreferencesUpdate} className="btn btn-primary btn-md">
                    Save Preferences
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Settings; 